import React from 'react';
import styled from 'styled-components';
import isEmpty from 'lodash/isEmpty';
import {
  components as ReactSelectComponents,
  ControlProps,
  DropdownIndicatorProps,
  GroupBase,
  SingleValueProps,
  StylesConfig,
  InputProps,
} from 'react-select';
import { BasicEntity } from '../../EntityLabels';
import { SvgIcon } from '../../Icon';
import { theme } from '../../../styles/theme';

export const getDefaultStyles = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  zIndex: number,
): StylesConfig<Option, IsMulti, Group> => ({
  control: (base, prop) => {
    const baseStyles = {
      ...base,
      minWidth: '240px',
      minHeight: '28px',
      borderColor: prop.menuIsOpen
        ? theme.colors.mainBlue
        : theme.colors.border,
      borderRadius: '4px',
      boxShadow: 'none',
      color: theme.colors.secondary900,
      '&:hover': {
        borderColor: theme.colors.mainBlue,
        cursor: 'pointer',
      },
      'body.dark &': {
        backgroundColor: theme.colors.level0,
      },
    };

    if (prop.isDisabled) {
      return {
        ...baseStyles,
        backgroundColor: 'transparent',
        '&:hover': {
          borderColor: 'inherit',
        },
        'body.dark &': {
          backgroundColor: theme.colors.level0,
          color: theme.colors.secondary400,
        },
      };
    }

    return baseStyles;
  },
  valueContainer: (base) => ({
    ...base,
    gap: '4px',
    padding: '2px 3px',
  }),
  placeholder: (base) => ({
    ...base,
    marginLeft: '4px',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '18px',
    color: theme.colors.secondary400,
  }),
  loadingMessage: (base) => ({
    ...base,
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '18px',
    color: theme.colors.secondary400,
  }),
  input: (base, prop) => ({
    ...base,
    marginTop: 0,
    marginLeft: prop?.selectProps?.isMulti && !prop.hasValue ? '4px' : 0,
    marginBottom: 0,
    paddingTop: 0,
    paddingBottom: 0,
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: '18px',
    color: theme.colors.secondary900,
    caretColor: theme.colors.secondary900,
  }),
  menu: (base) => ({
    ...base,
    zIndex,
    marginTop: 0,
    backgroundColor: theme.colors.level2,
    borderRadius: '4px',
    boxShadow: '0 4px 10px 0 rgba(0, 0, 0, 0.1)',
  }),
  menuList: (base) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
    '&::-webkit-scrollbar': {
      width: '2px !important',
      height: '2px !important',
      borderRadius: '2px !important',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'rgba(255, 255, 255, 0.4)',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: '#c2c2c2 !important',
    },
  }),
  noOptionsMessage: (base) => ({
    ...base,
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: 1.5,
    color: theme.colors.secondary400,
  }),
  option: (base, props) => ({
    ...base,
    minHeight: '28px',
    padding: '6px 8px',
    fontSize: '12px',
    fontWeight: '400',
    lineHeight: 'normal',
    color: props.isDisabled
      ? theme.colors.secondary400
      : props.isSelected
        ? theme.colors.mainBlue
        : theme.colors.secondary800,
    backgroundColor: 'unset',
    wordBreak: 'break-all',
    '&:hover': {
      backgroundColor: theme.colors.blue500,
      cursor: props.isDisabled ? 'not-allowed' : 'pointer',
      color: theme.colors.mainBlue,
    },
    '&:first-of-type': {
      borderTopRightRadius: '4px',
      borderTopLeftRadius: '4px',
    },
    '&:last-of-type': {
      borderBottomRightRadius: '4px',
      borderBottomLeftRadius: '4px',
    },
  }),
  multiValue: (base) => ({
    ...base,
    margin: 0,
    backgroundColor: 'unset',
  }),
  singleValue: (base, prop) => ({
    ...base,
    marginRight: 0,
    marginLeft: prop?.selectProps?.isSearchable ? '2px' : 0,
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: 1.5,
    color: theme.colors.secondary900,
  }),
  group: (base) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
    borderBottom: `1px solid ${theme.colors.border}`,
  }),
  groupHeading: (base) => ({
    ...base,
    marginBottom: 0,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 4px 0 0',
  }),
  // When menuPortalTarget is used, we want menu appear on top
  // of Popover or Modal. So that's why adjusting the z-index
  menuPortal: (base) => ({
    ...base,
    zIndex,
  }),
});

export const getBorderlessStyles = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  zIndex: number,
): StylesConfig<Option, IsMulti, Group> => ({
  control: (base) => ({
    ...base,
    boxShadow: 'none',
    border: 'none',
    '&:hover': {
      cursor: 'pointer',
    },
  }),
  valueContainer: (base) => ({
    ...base,
    gap: '4px',
    padding: '2px 3px',
  }),
  placeholder: (base) => ({
    ...base,
    marginLeft: '4px',
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: 1.5,
    color: theme.colors.secondary600,
  }),
  loadingMessage: (base) => ({
    ...base,
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: 1.5,
    color: theme.colors.secondary600,
  }),
  input: (base, prop) => ({
    ...base,
    marginTop: 0,
    marginLeft: prop?.selectProps?.isMulti && !prop.hasValue ? '4px' : 0,
    marginBottom: 0,
    paddingTop: 0,
    paddingBottom: 0,
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: 1.5,
    color: theme.colors.secondary900,
    caretColor: theme.colors.mainBlue,
  }),
  menu: (base) => ({
    ...base,
    // above the step splitter line
    zIndex,
    marginTop: 0,
    borderRadius: 0,
    boxShadow: '0 2px 6px 0 rgba(0, 0, 0, 0.15)',
    width: 'max-content',
  }),
  menuList: (base) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
    width: 'max-content',
  }),
  noOptionsMessage: (base) => ({
    ...base,
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: 1.5,
    color: theme.colors.secondary400,
  }),
  option: (base, props) => ({
    ...base,
    minHeight: '30px',
    padding: '6px 8px',
    fontSize: '12px',
    fontWeight: '500',
    lineHeight: 1.5,
    color: props.isDisabled
      ? theme.colors.secondary400
      : theme.colors.secondary800,
    backgroundColor: 'transparent',
    wordBreak: 'break-all',
    '&:hover': {
      backgroundColor: theme.colors.blue500,
      cursor: props.isDisabled ? 'not-allowed' : 'pointer',
      color: theme.colors.mainBlue,
    },
  }),
  multiValue: (base) => ({
    ...base,
    margin: 0,
    backgroundColor: 'unset',
  }),
  singleValue: (base, prop) => ({
    ...base,
    marginRight: 0,
    marginLeft: prop?.selectProps?.isSearchable ? '2px' : 0,
  }),
  group: (base) => ({
    ...base,
    paddingTop: 0,
    paddingBottom: 0,
    borderBottom: `1px solid ${theme.colors.border}`,
  }),
  groupHeading: (base) => ({
    ...base,
    marginBottom: 0,
  }),
  dropdownIndicator: (base) => ({
    ...base,
    padding: '0 4px 0 0',
  }),
  // When menuPortalTarget is used, we want menu appear on top
  // of Popover or Modal. So that's why adjusting the z-index
  menuPortal: (base) => ({
    ...base,
    zIndex,
  }),
});

export const BasicSingleValue = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  children,
  ...singleValueProps
}: SingleValueProps<Option, IsMulti, Group>) => {
  return (
    <ReactSelectComponents.SingleValue {...singleValueProps}>
      <BasicEntity
        onClear={
          singleValueProps.selectProps.isClearable === true
            ? singleValueProps.clearValue
            : undefined
        }
      >
        {children}
      </BasicEntity>
    </ReactSelectComponents.SingleValue>
  );
};

export const EmptyComponent = () => {
  return null;
};

// Common input for all react-select components
// This component disable safari auto complete
// name="search" is the fix to prevents safari from showing auto complete
export const SafeSearchInput = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  children,
  ...inputProps
}: InputProps<Option, IsMulti, Group>) => {
  return (
    <ReactSelectComponents.Input
      {...inputProps}
      autoComplete="off"
      name="search"
    />
  );
};

const DropdownArrowContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
`;

export const BasicDropdownIndicator = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>({
  props,
  dropdownArrowSize = 10,
  color,
}: {
  props: DropdownIndicatorProps<Option, IsMulti, Group>;
  dropdownArrowSize?: number;
  color?: string;
}) => {
  return (
    <ReactSelectComponents.DropdownIndicator {...props}>
      <DropdownArrowContainer>
        <SvgIcon
          name="caret-down-fill"
          width={dropdownArrowSize}
          height={dropdownArrowSize}
          isActive={!isEmpty(color) || props.selectProps.menuIsOpen}
          activeColor={!isEmpty(color) ? color : undefined}
        />
      </DropdownArrowContainer>
    </ReactSelectComponents.DropdownIndicator>
  );
};

export const ControlWithSearchIcon = <
  Option,
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(
  props: ControlProps<Option, IsMulti, Group>,
) => {
  const { isFocused } = props;
  return (
    <ReactSelectComponents.Control {...props}>
      <span style={{ paddingTop: '2px', paddingLeft: '4px' }}>
        <SvgIcon
          name="magnifying-glass"
          width={12}
          height={12}
          fill={isFocused ? theme.colors.mainBlue : theme.colors.secondary400}
        />
      </span>
      {props.children}
    </ReactSelectComponents.Control>
  );
};
